import * as api from "./client.js";
import { APPROVAL_SYNC_FAILED_CODE } from "./pipelineBatchUpdates.service.js";

export type MoveBpaTarget = {
  practiceAreaId: number;
  practiceAreaName: string;
  tableId: number;
  tableName: string;
};

export type MoveBpaRequest = {
  firmRef: string;
  publicationId: number;
  sourcePracticeAreaIds: number[];
  target: MoveBpaTarget;
  expectedItems?: number;
  dryRun: boolean;
};

export const MOVE_BPA_FAMILY_ACTIONS = [
  "move",
  "already_in_place",
  "approval_sync_failed",
  "family_changed",
  "approval_applied_pipeline_failed",
] as const;

export type MoveBpaFamilyAction = (typeof MOVE_BPA_FAMILY_ACTIONS)[number];

const SETTLED_FAMILY_ACTIONS: readonly string[] = ["move", "already_in_place"];

export type MoveBpaFamily = {
  rootId: string;
  externalId: string | null;
  childIds: string[];
  fromPracticeAreaId: number | null;
  action: MoveBpaFamilyAction;
};

export type MoveBpaMatched = { roots: number; children: number; alreadyInPlace: number };

export type MoveBpaMoved = {
  approvalRequests: number;
  testimonials: number;
  processingItems: number;
};

export type MoveBpaApprovalSyncFailure = { externalIds: string[]; error: string };

export type MoveBpaPipelineApply = { error: string | null };

export type MoveBpaResponse = {
  dryRun: boolean;
  matched: MoveBpaMatched;
  moved: MoveBpaMoved;
  families: MoveBpaFamily[];
  approvalSync: { attempted: number; failed: MoveBpaApprovalSyncFailure[] };
  pipelineApply: MoveBpaPipelineApply;
  warnings: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasNumbers(value: unknown, keys: readonly string[]): boolean {
  return isRecord(value) && keys.every(key => typeof value[key] === "number");
}

function isFamily(value: unknown): value is MoveBpaFamily {
  return (
    isRecord(value) &&
    typeof value.rootId === "string" &&
    Array.isArray(value.childIds) &&
    typeof value.action === "string"
  );
}

function isSyncFailure(value: unknown): value is MoveBpaApprovalSyncFailure {
  return isRecord(value) && Array.isArray(value.externalIds) && typeof value.error === "string";
}

function isPipelineApply(value: unknown): value is MoveBpaPipelineApply {
  return isRecord(value) && (value.error === null || typeof value.error === "string");
}

function isMoveBpaResponse(value: unknown): value is MoveBpaResponse {
  return (
    isRecord(value) &&
    typeof value.dryRun === "boolean" &&
    hasNumbers(value.matched, ["roots", "children", "alreadyInPlace"]) &&
    hasNumbers(value.moved, ["approvalRequests", "testimonials", "processingItems"]) &&
    Array.isArray(value.families) &&
    value.families.every(isFamily) &&
    isRecord(value.approvalSync) &&
    Array.isArray(value.approvalSync.failed) &&
    value.approvalSync.failed.every(isSyncFailure) &&
    isPipelineApply(value.pipelineApply) &&
    Array.isArray(value.warnings) &&
    value.warnings.every(warning => typeof warning === "string")
  );
}

export function moveBpaSyncFailureReport(err: unknown): MoveBpaResponse | null {
  if (!(err instanceof Error)) return null;
  const { status, code, detailsRaw } = err as Error & {
    status?: unknown;
    code?: unknown;
    detailsRaw?: unknown;
  };
  if (status !== 503 || code !== APPROVAL_SYNC_FAILED_CODE) return null;
  return isMoveBpaResponse(detailsRaw) ? detailsRaw : null;
}

export function countFamiliesNeedingRerun(report: MoveBpaResponse): number {
  return report.families.filter(family => !SETTLED_FAMILY_ACTIONS.includes(family.action)).length;
}

export const pipelineMoveBpaService = {
  async moveBpa(body: MoveBpaRequest): Promise<MoveBpaResponse> {
    return api.post("/v1/content-processor/staff/testimonials/bulk/move-practice-area", body);
  },
};
